import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, UserRound } from "lucide-react";
import { useDealerContext } from "@/lib/useDealerContext";

const ROLE_LABEL = { DEALER_ADMIN: "Dealer Admin", LOCATION_MANAGER: "Location Manager", SALES_DESIGNER: "Sales / Designer" };

export default function Team() {
  const { me, locations, locLabel } = useDealerContext();
  const [users, setUsers] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", dealer_role: "SALES_DESIGNER", job_title: "", location_id: "" });
  const [saving, setSaving] = useState(false);
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessForm, setAccessForm] = useState({ dealer_role: "SALES_DESIGNER", job_title: "", location_id: "" });
  const [continuity, setContinuity] = useState(null); // {userId, name, detail}
  const [continuityChoice, setContinuityChoice] = useState("UNASSIGNED");

  const myRole = me?.dealer_role || (me?.location_scope === "location" ? "LOCATION_MANAGER" : "DEALER_ADMIN");
  const isAdmin = myRole === "DEALER_ADMIN";

  const load = () =>
    api.get("/dealer/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/dealer/users", {
        name: form.name.trim(),
        email: form.email.trim(),
        dealer_role: form.dealer_role,
        job_title: form.job_title.trim(),
        ...(form.dealer_role !== "DEALER_ADMIN" ? { location_id: form.location_id } : {}),
      });
      toast.success(`Invitation queued for ${data.email} — setup link valid 72 hours once delivered`);
      setInviteOpen(false);
      setForm({ name: "", email: "", dealer_role: "SALES_DESIGNER", job_title: "", location_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveAccess = async (e) => {
    e.preventDefault();
    if (!accessTarget) return;
    setSaving(true);
    try {
      await api.patch(`/dealer/users/${accessTarget.id}/scope`, {
        dealer_role: accessForm.dealer_role,
        job_title: accessForm.job_title.trim(),
        ...(accessForm.dealer_role !== "DEALER_ADMIN" ? { location_id: accessForm.location_id } : {}),
      });
      toast.success(`Access updated for ${accessTarget.name}`);
      setAccessTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (u, active, continuityPayload = null) => {
    try {
      await api.patch(`/dealer/users/${u.id}/status`, {
        active,
        ...(continuityPayload ? { continuity: continuityPayload } : {}),
      });
      toast.success(active ? `${u.name} activated` : `${u.name} deactivated`);
      setContinuity(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!active && detail?.code === "project_continuity_required") {
        setContinuity({ userId: u.id, name: u.name, detail });
        setContinuityChoice("UNASSIGNED");
        return;
      }
      toast.error(formatApiErrorDetail(detail));
    }
  };

  if (!isAdmin && me) {
    return (
      <div data-testid="team-page-denied" className="text-sm text-warmgrey">
        Team management requires the Dealer Admin role.
      </div>
    );
  }

  return (
    <div data-testid="dealer-team-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Company</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Team</h1>
          <p className="mt-2 max-w-xl text-sm text-warmgrey">
            Invite teammates and assign permission roles. Role determines authority, location determines scope — job title is descriptive only.
          </p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-button" className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover">
              <Plus className="mr-2 h-4 w-4" /> Invite teammate
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border bg-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-charcoal">Invite teammate</DialogTitle>
              <DialogDescription className="text-sm text-warmgrey">
                They receive a secure setup link by email — no password is ever shown here.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={invite} className="mt-2 space-y-5" data-testid="invite-user-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Name *</Label>
                  <Input required data-testid="invite-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Email *</Label>
                  <Input required type="email" data-testid="invite-email-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Permission role *</Label>
                <Select value={form.dealer_role} onValueChange={(v) => setForm((f) => ({ ...f, dealer_role: v, location_id: "" }))}>
                  <SelectTrigger data-testid="invite-role-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="SALES_DESIGNER" data-testid="invite-role-sales">Sales / Designer — one assigned location</SelectItem>
                    <SelectItem value="LOCATION_MANAGER" data-testid="invite-role-manager">Location Manager — one assigned location, can reassign owners</SelectItem>
                    <SelectItem value="DEALER_ADMIN" data-testid="invite-role-admin">Dealer Admin — all locations, company administration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.dealer_role !== "DEALER_ADMIN" && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Assigned location *</Label>
                  <Select value={form.location_id} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v }))}>
                    <SelectTrigger data-testid="invite-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id} data-testid={`invite-location-${l.id}`}>
                          {l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Job title <span className="normal-case text-muted-foreground">(optional — descriptive only)</span></Label>
                <Input data-testid="invite-job-title-input" value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))} placeholder="e.g. Senior Interior Designer" className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <Button type="submit" disabled={saving || (form.dealer_role !== "DEALER_ADMIN" && !form.location_id)} data-testid="invite-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invitation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!users ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : (
        <div className="mt-10 space-y-3" data-testid="team-list">
          {users.map((u) => (
            <Card key={u.id} className="border-border bg-white" data-testid={`team-user-${u.id}`}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div className="flex items-center gap-3">
                  <UserRound className="h-5 w-5 text-warmgrey" />
                  <div>
                    <p className="font-medium text-charcoal">{u.name}{u.id === me?.id ? " (you)" : ""}</p>
                    <p className="text-xs text-muted-foreground">
                      {u.email ? `${u.email} · ` : ""}{ROLE_LABEL[u.dealer_role]}
                      {u.dealer_role !== "DEALER_ADMIN" ? ` — ${locLabel(u.location_id) || "unassigned"}` : " — all locations"}
                      {u.job_title ? ` · ${u.job_title}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`badge-status ${!u.active ? "badge-neutral" : !u.invitation || u.invitation.state === "active" ? "badge-available" : "badge-reserved"}`}
                    data-testid={`team-status-${u.id}`}
                  >
                    {/* AC: ACTIVE only after successful setup — invited teammates show their lifecycle state */}
                    {!u.active ? "Deactivated"
                      : !u.invitation || u.invitation.state === "active" ? "Active"
                      : u.invitation.state === "failed" ? "Invite failed"
                      : u.invitation.state === "expired" ? "Invite expired"
                      : "Invited — setup pending"}
                  </span>
                  <Button
                    variant="outline" size="sm" data-testid={`team-edit-access-${u.id}`}
                    onClick={() => {
                      setAccessTarget(u);
                      setAccessForm({ dealer_role: u.dealer_role, job_title: u.job_title || "", location_id: u.location_id || "" });
                    }}
                    className="border-limestone text-charcoal hover:bg-sand"
                  >
                    Edit access
                  </Button>
                  {u.id !== me?.id && (
                    <Button
                      variant="outline" size="sm" data-testid={`team-toggle-${u.id}`}
                      onClick={() => toggleStatus(u, !u.active)}
                      className={u.active ? "border-limestone text-status-sold hover:bg-sand" : "border-limestone text-charcoal hover:bg-sand"}
                    >
                      {u.active ? "Deactivate" : "Activate"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!accessTarget} onOpenChange={(o) => !o && setAccessTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="team-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Edit access</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Dealer Admins have dealer-wide authority. Location Managers and Sales/Designers work within one assigned location.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveAccess} className="mt-2 space-y-5" data-testid="team-access-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Permission role</Label>
              <Select value={accessForm.dealer_role} onValueChange={(v) => setAccessForm((f) => ({ ...f, dealer_role: v, location_id: f.dealer_role === v ? f.location_id : "" }))}>
                <SelectTrigger data-testid="team-access-role-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="DEALER_ADMIN">Dealer Admin — all locations</SelectItem>
                  <SelectItem value="LOCATION_MANAGER">Location Manager — one assigned location</SelectItem>
                  <SelectItem value="SALES_DESIGNER">Sales / Designer — one assigned location</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {accessForm.dealer_role !== "DEALER_ADMIN" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Assigned location *</Label>
                <Select value={accessForm.location_id} onValueChange={(v) => setAccessForm((f) => ({ ...f, location_id: v }))}>
                  <SelectTrigger data-testid="team-access-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Job title <span className="normal-case text-muted-foreground">(descriptive only)</span></Label>
              <Input data-testid="team-access-job-title" value={accessForm.job_title} onChange={(e) => setAccessForm((f) => ({ ...f, job_title: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving || (accessForm.dealer_role !== "DEALER_ADMIN" && !accessForm.location_id)} data-testid="team-access-save" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save access"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!continuity} onOpenChange={(o) => !o && setContinuity(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="team-continuity-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Project continuity</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey" data-testid="team-continuity-copy">
              {continuity?.detail?.message} Projects are never deleted — notes and history keep the original attribution.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <Select value={continuityChoice} onValueChange={setContinuityChoice}>
              <SelectTrigger data-testid="team-continuity-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="UNASSIGNED" data-testid="team-continuity-unassigned">Leave unassigned — stays at the original location</SelectItem>
                {(continuity?.detail?.eligible_users || []).map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`team-continuity-option-${u.id}`}>Reassign to {u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="team-continuity-confirm"
              onClick={() =>
                toggleStatus({ id: continuity.userId, name: continuity.name }, false,
                  continuityChoice === "UNASSIGNED" ? { action: "unassign" } : { action: "reassign", to_user_id: continuityChoice })
              }
              className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover"
            >
              Deactivate {continuity?.name}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
